import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Star, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Simple star (1–5) + optional comment feedback prompt.
 *
 * Props:
 *  - open / onOpenChange: dialog state
 *  - orderId?: link feedback to a specific order
 *  - source?: 'web' | 'kiosk' | 'post_delivery' | 'table' | 'mobile'
 *  - authed?: when true uses /me/feedback (requires login); otherwise /feedback/public
 *  - onSubmitted?: callback after successful submit
 *  - title? / description?: optional UI overrides
 *  - askContact?: show optional name/phone fields (default: only for public)
 */
export default function FeedbackPrompt({
  open,
  onOpenChange,
  orderId,
  source = 'web',
  authed = false,
  onSubmitted,
  title,
  description,
  askContact,
}) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showContact = askContact ?? !authed;

  const reset = () => {
    setRating(0); setHover(0); setComment(''); setName(''); setPhone('');
  };

  const close = (next) => {
    onOpenChange?.(next);
    if (!next) reset();
  };

  const submit = async () => {
    if (!rating) { toast.error('Tap a star to rate'); return; }
    setSubmitting(true);
    try {
      if (authed) {
        await api.post('/me/feedback', {
          order_id: orderId,
          food_rating: rating,
          service_rating: rating,
          cleanliness_rating: rating,
          comment: comment || null,
        });
      } else {
        await api.post('/feedback/public', {
          rating,
          comment: comment || null,
          order_id: orderId,
          source,
          customer_name: name || null,
          customer_phone: phone || null,
        });
      }
      toast.success('Thanks for your feedback!');
      // Local memo so we don’t re-prompt for the same order on this device.
      if (orderId) {
        try { localStorage.setItem(`fb:${orderId}`, '1'); } catch (e) { /* ignore */ }
      }
      onSubmitted?.();
      close(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="feedback-prompt-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{title || 'How was your experience?'}</DialogTitle>
          <DialogDescription>{description || 'Your feedback helps us improve. Takes 5 seconds.'}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="flex items-center gap-1" data-testid="feedback-prompt-stars">
            {[1, 2, 3, 4, 5].map((i) => {
              const active = (hover || rating) >= i;
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`feedback-star-${i}`}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(i)}
                  className="rounded-md p-1.5 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  aria-label={`Rate ${i} star${i > 1 ? 's' : ''}`}
                >
                  <Star className={`h-8 w-8 ${active ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground/40'}`} />
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {rating ? `You rated ${rating} / 5` : 'Tap a star to rate'}
          </p>
          <div className="w-full space-y-2">
            <Textarea
              data-testid="feedback-comment-input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more (optional)…"
              maxLength={1000}
              rows={3}
            />
            {showContact && (
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="feedback-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" />
                <Input data-testid="feedback-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" />
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button data-testid="feedback-skip-button" variant="ghost" onClick={() => close(false)} disabled={submitting}>
            Maybe later
          </Button>
          <Button data-testid="feedback-submit-button" onClick={submit} disabled={submitting || !rating}>
            <Send className="mr-1.5 h-4 w-4" /> {submitting ? 'Sending…' : 'Send feedback'}
          </Button>
        </DialogFooter>
        <p className="flex items-center justify-center gap-1 pt-1 text-[10px] text-muted-foreground">
          <MessageSquare className="h-3 w-3" /> Anonymous unless you sign in
        </p>
      </DialogContent>
    </Dialog>
  );
}
